import BaseService from "../../base/base_service";
import IGeoRetryService from "./geo_retry_service_interface";
import {Check} from "@sparkware/app-utils";
import {geoRetryResponse} from "../oauth/user/user_service_interface";
import {IGeoRequestParams} from "./geo_service_interface";
import {SYSTEM_EVENTS} from "../../events/events";
import {GNWP_SOURCES} from "../../components/external_links/external_links_consts";
import {GEO_LOCATION} from "./geo_consts";

const ERROR_CODES_ELIGIBLE_FOR_RETRY: string[] = ["23","27","28","35"];
const MAX_RETRIES_PER_SESSION: number = 5;

export default class GeoRetryService extends BaseService implements IGeoRetryService{
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'GeoRetryService';

    private _retryCount: number = 0;
    private _previousKickReasonID: string = null;

    init(params) {
        super.init(params);
        this._subscribe();
    }

    /**
     * Check if the user is eligible for retry geo location.
     * @param kickReasonID
     */
    public isEligible(kickReasonID: string):boolean {
        if(Check.isNullOrUndefined(kickReasonID)) {
            // We need it for the option that user open a game and we don't know what is the reasonID for the geo failure.
            kickReasonID = this._previousKickReasonID;
        } else {
            this._previousKickReasonID = kickReasonID;
        }

        if(this._retryCount >= MAX_RETRIES_PER_SESSION) {
            this._sendAnalytics('max retries for retry is reached');
            return false;
        }

        return this._isRetryEnabled() && ERROR_CODES_ELIGIBLE_FOR_RETRY.includes(kickReasonID);
    }

    public getRetryMessageButtons(analyticsLabel: string): any[] {
        return [
            {
                name: this.serviceFactory.languageService().translation('retry').toUpperCase(),
                callback: () => {
                    this._sendAnalytics('retry button clicked', analyticsLabel);
                    this._retryCount++;
                    this.serviceFactory.geoLocationService().startRetryGeoProcess();
                }
            },
            {
                name: this.serviceFactory.languageService().translation('contact_us').toUpperCase(),
                type: "secondary",
                callback: () => {
                    this._sendAnalytics('contact us button clicked', analyticsLabel);
                    this.serviceFactory.openLinkService().openLink({
                        source: GNWP_SOURCES.CONTACT_US,
                        view: 'window'
                    });
                }
            }
        ]
    }

    public async initGeoRetry(): Promise<IGeoRequestParams> {
        this._sendAnalytics('call init geo retry api');
        const initRetryResponse: geoRetryResponse = await this.serviceFactory.userService().initGeoRetry();
        const accountData = this.serviceFactory.userService().getAccountData();

        return {
            mobileNumber:accountData.MobileNumber,
            licenseKey: initRetryResponse.GeoComplyLicense,
            locationTransactionId:initRetryResponse.LocationTransactionToken,
            isGeoLocationInProcess:true
        };
    }

    private _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOauthChange.bind(this))
    }

    private _onOauthChange() {
        this._retryCount = 0;
    }

    private _isRetryEnabled() {
        return this.serviceFactory.userService().isGeoRetryEnable();
    }

    private _sendAnalytics(action:string, label: string = null):void {
        const analyticsData:any = {
            CGPEventCategory: GEO_LOCATION.GA_GEO_LOCATION_CATEGORY_SERVICE,
            CGPEventAction: action,
            CGPEventLabel: label
        };

        this.serviceFactory.structuredLoggingService().getLogger(analyticsData).info();
    }
}